import React, { useState, useEffect, useCallback } from 'react';
import { Award, TrendingUp, CalendarCheck, Printer, BookOpen, AlertCircle, RefreshCw, Star, TrendingDown, Minus } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import api from '../../api';

const GRADE_CONFIG = {
  A1: { label: 'A1', bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500', pct: 95, stroke: '#10b981' },
  B2: { label: 'B2', bg: 'bg-blue-100',    text: 'text-blue-700',    bar: 'bg-blue-500',    pct: 83, stroke: '#3b82f6' },
  B3: { label: 'B3', bg: 'bg-blue-100',    text: 'text-blue-700',    bar: 'bg-blue-400',    pct: 78, stroke: '#60a5fa' },
  C4: { label: 'C4', bg: 'bg-amber-100',   text: 'text-amber-700',   bar: 'bg-amber-500',   pct: 68, stroke: '#f59e0b' },
  C5: { label: 'C5', bg: 'bg-amber-100',   text: 'text-amber-700',   bar: 'bg-amber-400',   pct: 63, stroke: '#fbbf24' },
  C6: { label: 'C6', bg: 'bg-amber-100',   text: 'text-amber-700',   bar: 'bg-amber-300',   pct: 58, stroke: '#fcd34d' },
  D7: { label: 'D7', bg: 'bg-orange-100',  text: 'text-orange-700',  bar: 'bg-orange-500',  pct: 48, stroke: '#f97316' },
  E8: { label: 'E8', bg: 'bg-orange-100',  text: 'text-orange-600',  bar: 'bg-orange-400',  pct: 38, stroke: '#fb923c' },
  F9: { label: 'F9', bg: 'bg-red-100',     text: 'text-red-700',     bar: 'bg-red-500',     pct: 25, stroke: '#ef4444' },
};

function getScorePct(score, best) {
  if (best != null) return Math.min(100, Math.round((best / 100) * 100));
  const g = GRADE_CONFIG[score];
  return g ? g.pct : 50;
}

export default function StudentResultsPage() {
  const [results,         setResults]         = useState(null);
  const [terms,           setTerms]           = useState([]);
  const [selectedTermId,  setSelectedTermId]  = useState('');
  const [selectedTermName,setSelectedTermName]= useState('');
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState('');
  const [history,         setHistory]         = useState({});

  useEffect(() => {
    api.get('/academics/current-session').then(d => {
      if (d.success) {
        setTerms(d.data.terms_for_year || []);
        const cur = d.data.term;
        if (cur) { setSelectedTermId(cur.id); setSelectedTermName(cur.name); }
      }
    }).catch(() => {});
    // Fetch sparkline history
    api.get('/exams/my-results/history').then(r => {
      if (r?.success) setHistory(r.data.history || {});
    }).catch(() => {});
  }, []);

  const fetchResults = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const url = selectedTermId ? `/exams/my-results?term_id=${selectedTermId}` : '/exams/my-results';
      const d = await api.get(url);
      if (d.success) {
        setResults(d);
        // Award XP based on average (once per term)
        if (d.overall_average != null && selectedTermId) {
          const action = d.overall_average >= 70 ? 'results_viewed_good'
                       : d.overall_average >= 50 ? 'results_viewed_pass'
                       : 'results_viewed_any';
          api.post('/student/xp/award', { action, reference_id: `results_${selectedTermId}` }).catch(() => {});
        }
      }
    } catch (err) {
      setError(err?.detail || 'Failed to load results');
    } finally { setLoading(false); }
  }, [selectedTermId]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  const termName = terms.find(t => t.id === selectedTermId)?.name || selectedTermName || 'Current Term';
  const avg      = results?.overall_average;
  const avgColor = avg >= 70 ? 'text-emerald-600' : avg >= 50 ? 'text-amber-600' : 'text-rose-600';
  const bestSubj = results?.subjects?.length
    ? results.subjects.reduce((a, b) => (b.best_score ?? 0) > (a.best_score ?? 0) ? b : a, results.subjects[0])
    : null;

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto space-y-5" data-testid="student-results-page">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900">My Results</h1>
          <p className="text-slate-500 text-sm mt-0.5">View your exam scores and performance</p>
        </div>
        <div className="flex items-center gap-2">
          {terms.length > 0 && (
            <select
              value={selectedTermId}
              onChange={e => {
                const t = terms.find(t => t.id === e.target.value);
                setSelectedTermId(e.target.value);
                setSelectedTermName(t?.name || '');
              }}
              className="text-sm border border-slate-200 rounded-xl px-3 py-2 bg-white text-slate-700 focus:ring-2 focus:ring-[#2563eb]/20 outline-none font-semibold"
              data-testid="results-term-select"
            >
              {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <a
            href="/student/report-card"
            className="flex items-center gap-1.5 bg-[#2563eb] text-white text-sm px-4 py-2 rounded-xl hover:bg-[#4058e8] transition-colors font-bold shadow-sm"
            data-testid="view-report-card-btn"
          >
            <Printer className="w-3.5 h-3.5" /> Report Card
          </a>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-[#2563eb] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-bold text-amber-800">No results found</p>
            <p className="text-sm text-amber-700 mt-1">{error}</p>
            <button onClick={fetchResults} className="mt-3 flex items-center gap-1.5 text-sm font-bold text-amber-700 hover:underline">
              <RefreshCw className="w-3.5 h-3.5" /> Try again
            </button>
          </div>
        </div>
      ) : results ? (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-gradient-to-br from-[#2563eb] to-[#60a5fa] rounded-3xl p-5 shadow-lg shadow-[#2563eb]/20" data-testid="stat-average">
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <p className="text-3xl font-black text-white leading-none">
                {avg != null ? `${avg}%` : '—'}
              </p>
              <p className="text-white/70 text-xs mt-1 font-semibold">Average Score</p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="stat-subjects">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
                <BookOpen className="w-4 h-4 text-[#2563eb]" />
              </div>
              <p className="text-3xl font-black text-slate-900 leading-none">{results.subjects?.length || 0}</p>
              <p className="text-slate-500 text-xs mt-1 font-semibold">Subjects</p>
            </div>

            <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid="stat-attendance">
              <div className="w-9 h-9 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
                <CalendarCheck className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-3xl font-black text-slate-900 leading-none">
                {results.attendance_rate != null ? `${results.attendance_rate}%` : '—'}
              </p>
              <p className="text-slate-500 text-xs mt-1 font-semibold">Attendance</p>
            </div>
          </div>

          {/* Best Subject Callout */}
          {bestSubj && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 flex items-center gap-3">
              <Star className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <span className="font-bold">Best subject:</span> {bestSubj.subject}
                {bestSubj.best_score != null && <span> — {bestSubj.best_score}/100</span>}
              </p>
            </div>
          )}

          {/* Term label */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-700">{termName} Results</span>
            {results.student && (
              <span className="text-xs text-slate-400">· {results.student.class_name} · {results.student.admission_number}</span>
            )}
          </div>

          {/* Subjects */}
          {!results.subjects?.length ? (
            <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center shadow-sm">
              <Award className="w-10 h-10 text-slate-200 mx-auto mb-3" />
              <p className="text-slate-500 font-bold">No results for this term yet</p>
              <p className="text-sm text-slate-400 mt-1">Results will appear here once your teachers enter scores</p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.subjects.map((subj, i) => {
                const gcfg  = GRADE_CONFIG[subj.grade] || { bg: 'bg-slate-100', text: 'text-slate-600', bar: 'bg-slate-400', pct: 50 };
                const pct   = getScorePct(subj.grade, subj.best_score);
                return (
                  <div key={i} className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`result-row-${i}`}>
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="font-bold text-slate-900">{subj.subject}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{subj.remark}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {subj.best_score != null && (
                          <span className="font-black text-slate-800 text-lg leading-none">
                            {subj.best_score}<span className="text-slate-400 font-normal text-sm">/100</span>
                          </span>
                        )}
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${gcfg.bg} ${gcfg.text}`}>
                          {subj.grade}
                        </span>
                      </div>
                    </div>
                    {/* Score bar */}
                    <div className="w-full bg-slate-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${gcfg.bar} transition-all duration-500`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {subj.average != null && (
                      <p className="text-xs text-slate-400 mt-1.5">Class average: {subj.average}</p>
                    )}
                    {/* Sparkline trend */}
                    {history[subj.subject]?.length >= 2 && (
                      <div className="mt-3 pt-3 border-t border-slate-50">
                        <p className="text-[10px] text-slate-400 mb-1 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" /> Trend ({history[subj.subject].length} terms)
                        </p>
                        <div style={{ height: 44 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={history[subj.subject]} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                              <Tooltip
                                content={({ active, payload }) =>
                                  active && payload?.[0] ? (
                                    <div className="bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs shadow-sm">
                                      <p className="font-bold text-slate-700">{payload[0].value}%</p>
                                      <p className="text-slate-400">{payload[0].payload.term_name}</p>
                                    </div>
                                  ) : null
                                }
                              />
                              <Line
                                type="monotone"
                                dataKey="avg"
                                stroke={gcfg.stroke || '#6366f1'}
                                strokeWidth={2}
                                dot={{ r: 3, fill: gcfg.stroke || '#6366f1', strokeWidth: 0 }}
                                activeDot={{ r: 4 }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Overall Average */}
              {avg != null && (
                <div className="bg-gradient-to-r from-[#EFF6FF] to-[#EFF6FF] rounded-3xl p-5 border border-[#2563eb]/10 flex items-center justify-between">
                  <p className="font-bold text-slate-700">Overall Average</p>
                  <p className={`text-2xl font-black ${avgColor}`}>{avg}%</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
